import { Card } from '@delmonicos/shared-design-system-react/src/components/card/card';
import { DatePickerInput } from '@delmonicos/shared-design-system-react/src/components/date-picker-input/date-picker-input';
import { ModeEnum } from '@delmonicos/shared-design-system-react/src/components/date-picker/date-picker';
import { Dropdown } from '@delmonicos/shared-design-system-react/src/components/dropdown/dropdown';
import { TextInput } from '@delmonicos/shared-design-system-react/src/components/input/text-input';
import {
  GapEnum,
  Stack,
} from '@delmonicos/shared-design-system-react/src/components/stack/stack';
import { TextArea } from '@delmonicos/shared-design-system-react/src/components/text-area/text-area.component';
import {
  Text,
  TextColor,
  TextFontSize,
} from '@delmonicos/shared-design-system-react/src/components/typography/text/text';
import { AccentTitle } from '@delmonicos/shared-design-system-react/src/components/typography/title/accent-title/accent-title';
import { useUpdateTokenFormValidation } from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/inputs/token/update-token.form-validation';
import { UpdateTokenModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/inputs/token/update-token.model';
import { WhitelistType } from '@delmonicos/shared-sdk/libs/cpms/src/libs/ocpi/src/enums/ocpi-protocol-enums';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

type TokenUpdateFormModel = Omit<UpdateTokenModel, 'customerId' | 'networkId'>;

export const TokenUpdateFormStep1ControllerInformationFragment =
  (): React.JSX.Element => {
    const { t } = useTranslation();
    const { control } = useFormContext<TokenUpdateFormModel>();
    const {
      tokenExpirationDate,
      tokenWhitelist,
      tokenFirstName,
      tokenLastName,
      tokenVehicle,
      tokenComment,
    } = useUpdateTokenFormValidation();

    return (
      <Card gap={GapEnum.L} direction="column" flex={1}>
        <AccentTitle
          icon="info-circle"
          text={t('$.token-update.step-1.information.title')}
        />

        <Stack direction="column" gap={GapEnum.M}>
          <Controller
            name="token.whitelist"
            control={control}
            rules={tokenWhitelist}
            render={({ field, fieldState: { error } }) => (
              <Stack gap={GapEnum.XS} direction="column">
                <Text fontSize={TextFontSize.Small} uppercase>
                  {t('delmonicos-shared-sdk.outputs-token.whitelist')}{' '}
                  <Text color={TextColor.Error}>*</Text>
                </Text>
                <Dropdown
                  {...field}
                  value={field.value ?? ''}
                  onChange={(value) => field.onChange(value ?? '')}>
                  <Dropdown.Option hidden disabled key="" value="" />
                  {Object.values(WhitelistType).map((ty) => (
                    <Dropdown.Option key={ty} value={ty}>
                      {ty}
                    </Dropdown.Option>
                  ))}
                </Dropdown>
                {error?.message && (
                  <Text color={TextColor.Error}>{error.message}</Text>
                )}
              </Stack>
            )}
          />
          <Controller
            name="token.expirationDate"
            control={control}
            rules={tokenExpirationDate}
            render={({ field, fieldState: { error } }) => (
              <Stack gap={GapEnum.XS} direction="column">
                <Text fontSize={TextFontSize.Small} uppercase>
                  {t('delmonicos-shared-sdk.outputs-token.expiration-date')}
                </Text>
                <DatePickerInput
                  mode={ModeEnum.Single}
                  selected={field.value ? new Date(field.value) : undefined}
                  onSelect={(value) => field.onChange(value)}
                />
                {error?.message && (
                  <Text color={TextColor.Error}>{error.message}</Text>
                )}
              </Stack>
            )}
          />
          <Controller
            name="token.firstName"
            control={control}
            rules={tokenFirstName}
            render={({ field, fieldState: { error } }) => (
              <TextInput
                label={t('delmonicos-shared-sdk.outputs-token.first-name')}
                placeholder={t(
                  'delmonicos-shared-sdk.outputs-token.first-name',
                )}
                required={!!tokenFirstName?.required}
                errorMessage={error?.message}
                {...field}
                value={field.value ?? ''}
                onChange={(e) => {
                  field.onChange(e.target.value === '' ? '' : e.target.value);
                }}
              />
            )}
          />
          <Controller
            name="token.lastName"
            control={control}
            rules={tokenLastName}
            render={({ field, fieldState: { error } }) => (
              <TextInput
                label={t('delmonicos-shared-sdk.outputs-token.last-name')}
                placeholder={t('delmonicos-shared-sdk.outputs-token.last-name')}
                required={!!tokenLastName?.required}
                errorMessage={error?.message}
                {...field}
                value={field.value ?? ''}
                onChange={(e) => {
                  field.onChange(e.target.value === '' ? '' : e.target.value);
                }}
              />
            )}
          />
          <Controller
            name="token.vehicle"
            control={control}
            rules={tokenVehicle}
            render={({ field, fieldState: { error } }) => (
              <TextInput
                label={t('delmonicos-shared-sdk.outputs-token.vehicle')}
                placeholder={t('delmonicos-shared-sdk.outputs-token.vehicle')}
                required={!!tokenVehicle?.required}
                errorMessage={error?.message}
                {...field}
                value={field.value ?? ''}
                onChange={(e) => {
                  field.onChange(e.target.value === '' ? '' : e.target.value);
                }}
              />
            )}
          />
          <Controller
            name="token.comment"
            control={control}
            rules={tokenComment}
            render={({ field, fieldState: { error } }) => (
              <Stack gap={GapEnum.XS} direction="column">
                <TextArea
                  label={t('delmonicos-shared-sdk.outputs-token.comment')}
                  placeholder={t('delmonicos-shared-sdk.outputs-token.comment')}
                  required={!!tokenComment?.required}
                  {...field}
                  value={field.value ?? ''}
                  onChange={(e) => {
                    field.onChange(e.target.value === '' ? '' : e.target.value);
                  }}
                />
                {error?.message && (
                  <Text color={TextColor.Error}>{error.message}</Text>
                )}
              </Stack>
            )}
          />
        </Stack>
      </Card>
    );
  };

export const TokenUpdateFormStep1Controller = (): React.JSX.Element => {
  return (
    <Stack gap={GapEnum.L} direction="column" flex={1}>
      <TokenUpdateFormStep1ControllerInformationFragment />
    </Stack>
  );
};
